import { Listbox } from "@headlessui/react";
import React, { useState, useEffect } from 'react';
import { Package, ChevronDown, Info, MapPin, Calendar, Clock, Box, User, Phone, CheckCircle2, Loader2 } from 'lucide-react';
import Sidebar from '../../../component/b2b/sideBar/b2bSideBar';
import NavBar from '../../../component/b2b/navBar/b2bNavBar';
import { useMyContext } from '../../../context/MyContext';
import { toast } from 'react-toastify';
import '../../../css/custom-scrollbar.css'
import { Base_Url, createpickuprequest, b2bWarehouseListAll, pickrequestList } from '../../../config/config';

const PickupDetailsPage = () => {
    const [query, setQuery] = useState("");
    const [dates, setDates] = useState([]);
    const { isDarkMode, isToggleSidebar, selectedCft } = useMyContext();
    const [warehouses, setWarehouses] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState('');
    const [selectedCourier, setSelectedCourier] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(null);
    const [boxCount, setBoxCount] = useState('');
    const [selectedSlot, setSelectedSlot] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [pickupRequests, setPickupRequests] = useState([]);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const limitNumber = 10;

    const token = localStorage.getItem("token");
    const cus_id = localStorage.getItem("cus_id");


    useEffect(() => {
        fetchPickupRequests(currentPage);
    }, [currentPage]);

    const fetchPickupRequests = async (pageNumber) => {
        setLoading(true);
        try {
            const url = `${Base_Url}${pickrequestList}?page=${pageNumber}&limit=${limitNumber}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    cus_id: cus_id,
                    token: token,
                },
            });

            const result = await response.json();

            if (result.status === 'SUCCESS' && result.data) {
                setPickupRequests(result.data);
                setTotalPages(result.pagination.totalPages);
                setTotalItems(result.pagination.totalItems);
            } else {
                console.error('Failed to fetch pickup requests:', result.message);
                setPickupRequests([]);
            }
        } catch (error) {
            console.error('Error fetching pickup requests:', error);
            setPickupRequests([]);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'created':
                return 'bg-red-600 text-white';
            case 'pending':
                return 'bg-white text-black border-black border-dashed dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-400/40 dark:border-solid';
            case 'completed':
                return 'bg-black text-white border-black dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-400/40';
            case 'cancelled':
                return 'bg-white text-gray-400 border-gray-300 line-through dark:bg-red-500/15 dark:text-red-300 dark:border-red-400/40';
            default:
                return 'bg-white text-gray-600 border-gray-300 dark:bg-indigo-500/15 dark:text-indigo-200 dark:border-indigo-400/40';
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    useEffect(() => {
        fetchWarehouses();
    }, []);


    useEffect(() => {
        setDates(generateDates());
    }, []);

    const fetchWarehouses = async () => {
        try {
            const response = await fetch(Base_Url + b2bWarehouseListAll, {
                method: "GET",
                headers: {
                    "token": token,
                    "cus_id": cus_id,
                    "Content-Type": "application/json"
                }
            });
            const data = await response.json();
            if (data.status === 'SUCCESS' && data.data) {
                setWarehouses(data.data);
            }
        } catch (error) {
            console.error('Error fetching warehouses:', error);
        } finally {
            setLoading(false);
        }
    };


    const getRandomTimeInSlot = (slot) => {
        const startHour = slot.start;
        const endHour = slot.end;

        const randomHour = Math.floor(Math.random() * (endHour - startHour)) + startHour;
        const randomMinute = Math.floor(Math.random() * 60);

        return `${String(randomHour).padStart(2, "0")}:${String(randomMinute).padStart(2, "0")}:00`;
    };


    const handleCreateRequest = async () => {
        if (!selectedSlot) {
            toast.error("Please select a pickup slot", { position: "top-right" });
            return;
        }
        const slot = pickupSlots.find((s) => s.value === selectedSlot);
        if (!slot) {
            toast.error("Invalid slot", { position: "top-right" });
            return;
        }
        console.log("Submitting warehouse_id:", selectedOrder);

        setIsSubmitting(true);
        try {
            const selectedFullDate = new Date();
            selectedFullDate.setDate(selectedFullDate.getDate() + selectedDate);

            const pickupDateFormatted = selectedFullDate.toISOString().split("T")[0];
            const pickupStartTime = getRandomTimeInSlot(slot);

            const requestData = {
                warehouse_id: selectedOrder,
                pickup_date: pickupDateFormatted,
                start_time: pickupStartTime,
                retailer_id: cus_id,
                box_count: parseInt(boxCount),
                courier_partner: selectedCourier,
                type: selectedCft || ''
            };
            const response = await fetch(Base_Url + createpickuprequest, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "cus_id": localStorage.getItem("cus_id"),
                    "token": localStorage.getItem("token"),
                },
                body: JSON.stringify(requestData),
            });

            const data = await response.json();

            if (data.status === "success") {
                toast.success(data.message, { position: "top-right" });
                setSelectedDate(null);
                setSelectedSlot("");
                setBoxCount("");
            } else {
                toast.error(data.message || "Failed to create pickup request", { position: "top-right" });
            }
        } catch (error) {
            console.error("Error creating pickup request:", error);
            toast.error("An error occurred while creating the pickup request", { position: "top-right" });
        } finally {
            setIsSubmitting(false);
        }
    };

    const cftTypeMap = { 6: "lite", 10: "heavy", 15: "dense" };

    const filteredWarehouses = warehouses.filter((warehouse) => {
        const text = `
        ${warehouse.warehouse_id}
        ${warehouse?.retailer_id}
        ${warehouse?.name}
        ${warehouse.pin_code}
    `.toLowerCase();

        const matchesSearch = text.includes(query.toLowerCase());
        const matchesCft = !selectedCft || warehouse.cft_type?.toLowerCase() === cftTypeMap[selectedCft];

        return matchesSearch && matchesCft;
    });

    const generateDates = (daysCount = 6) => {
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

        const today = new Date();
        const result = [];

        for (let i = 0; i < daysCount; i++) {
            const current = new Date();
            current.setDate(today.getDate() + i);

            result.push({
                day: i === 0 ? "Today" : days[current.getDay()],
                date: current.getDate(),
                month: months[current.getMonth()]
            });
        }

        return result;
    };

    const pickupSlots = [
        { value: 'morning', label: 'Morning (7 AM - 10 PM)', start: 7, end: 10 },
        { value: 'morning', label: 'Morning (10 AM - 2 PM)', start: 10, end: 14 },
        { value: 'evening', label: 'Evening (2 PM - 6 PM)', start: 14, end: 18 },
        { value: 'night1', label: 'Night (6 PM - 9 PM)', start: 18, end: 21 },
        { value: 'night2', label: 'Night (9 PM - 12 AM)', start: 21, end: 24 }
    ];
    const glass =
        "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
        "border border-white/60 dark:border-white/10 " +
        "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";

    return (
        <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-black' : 'bg-white'}`}>
            <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-200/30 dark:bg-indigo-500/10 blur-3xl" />

            <NavBar />

            <div className="flex flex-1 overflow-hidden">

              <div
            className={`${isToggleSidebar ? "md:w-64" : "md:w-20"} w-0 m-0 md:m-3 md:rounded-3xl ${glass} overflow-visible md:overflow-hidden transition-all duration-300 ease-in-out flex-shrink-0`}
          
          >
            <Sidebar />
          </div>

                <div
                    className={`flex-1 p-3 md:p-2 overflow-auto transition-all duration-300 custom-scrollbar ${isToggleSidebar ? 'md:ml-0' : 'md:ml-0'} ${isDarkMode ? "dark:bg-black dark:text-white" : "bg-gray-200"}`}
                >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-4">
                        {/* Main Form Section */}
                        <div className="lg:col-span-1">
                            <div className="bg-white dark:bg-gradient-to-br dark:from-[#0b0b14] dark:via-[#0d0d18] dark:to-[#0b0b14] rounded-xl border border-gray-200 dark:border-indigo-500/25 dark:shadow-[0_4px_20px_rgba(79,70,229,0.15)] p-4 md:p-4">

                                <div className="flex items-center gap-2.5 mb-2 pb-1 border-b border-gray-100 dark:border-indigo-500/20">
                                    <div className="p-1.5 bg-black dark:bg-gradient-to-br dark:from-indigo-600 dark:to-purple-600 dark:shadow-lg dark:shadow-indigo-900/50 rounded-lg">
                                        <Package className="w-4 h-4 text-white" />
                                    </div>
                                    <h2 className="text-[13px] font-semibold text-black dark:text-white tracking-tight uppercase">Pickup Details</h2>
                                </div>

                                {/* Select Order - Mobile pe stack, Desktop pe side by side */}
                                <div className="flex flex-col md:flex-row gap-3 w-full">

                                    {/* WAREHOUSE DROPDOWN — FIRST */}
                                    <div className="flex-1">
                                        <label className="text-[8px] font-medium text-gray-500 dark:text-indigo-200/80 mb-1 block uppercase tracking-wide">
                                            Select Warehouse
                                        </label>

                                        <div className="relative">
                                            <Listbox value={selectedOrder} onChange={setSelectedOrder}>
                                                <div className="relative">

                                                    {/* Button */}
                                                    <Listbox.Button className="w-full px-2.5 py-2 md:py-1.5 bg-white dark:bg-[#1a1a2e] border border-gray-300 dark:border-indigo-400/50 rounded-md flex justify-between items-center text-[11px] text-black dark:text-white hover:border-black dark:hover:border-indigo-400 transition-colors">
                                                        <span className={selectedOrder ? "text-black dark:text-white" : "text-gray-400 dark:text-indigo-300/50"}>
                                                            {selectedOrder || "Select Warehouse"}
                                                        </span>
                                                        <ChevronDown className="w-3.5 h-3.5 text-gray-400 dark:text-indigo-300" />
                                                    </Listbox.Button>

                                                    {/* Dropdown */}
                                                    <Listbox.Options className="absolute z-50 mt-1.5 w-full bg-white dark:bg-gradient-to-br dark:from-[#0b0b14] dark:via-[#0d0d18] dark:to-[#0b0b14] border border-black dark:border-indigo-500/40 rounded-md shadow-[0_4px_20px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_40px_rgba(79,70,229,0.35)] max-h-64 overflow-auto">

                                                        {/* Search Box */}
                                                        <div className="sticky top-0 bg-white dark:bg-[#0b0b14] p-1.5 border-b border-gray-100 dark:border-indigo-500/20">
                                                            <input
                                                                type="text"
                                                                placeholder="Search Warehouse..."
                                                                value={query}
                                                                onChange={(e) => setQuery(e.target.value)}
                                                                className="w-full px-2 py-1 border border-gray-200 dark:border-indigo-400/40 rounded text-[11px] bg-white dark:bg-[#1a1a2e] text-black dark:text-white placeholder-gray-400 dark:placeholder-indigo-300/50 focus:outline-none focus:border-black dark:focus:border-indigo-400"
                                                            />
                                                        </div>

                                                        {/* Options */}
                                                        {filteredWarehouses.length === 0 ? (
                                                            <p className="p-3 text-[11px] text-gray-400 dark:text-indigo-300/60 text-center">No results found</p>
                                                        ) : (
                                                            filteredWarehouses.map((warehouse) => (
                                                                <Listbox.Option
                                                                    key={warehouse.warehouse_id}
                                                                    value={warehouse.warehouse_id}
                                                                    className={({ active }) =>
                                                                        `cursor-pointer px-2.5 py-1.5 border-b border-gray-50 dark:border-indigo-500/15 last:border-none text-[11px] ${active ? "bg-black dark:bg-indigo-600 text-white" : "text-black dark:text-indigo-100"
                                                                        }`
                                                                    }
                                                                >
                                                                    {({ active }) => (
                                                                        <>
                                                                            <p className="font-semibold">
                                                                                {warehouse.warehouse_id}
                                                                            </p>
                                                                            <p className={`text-[9px] ${active ? "text-gray-300" : "text-gray-500 dark:text-indigo-300/60"}`}>
                                                                                {warehouse.retailer_id} • {warehouse.name}
                                                                            </p>
                                                                            <p className={`text-[9px] ${active ? "text-gray-300" : "text-gray-500 dark:text-indigo-300/60"}`}>
                                                                                pin {warehouse.pin_code}
                                                                            </p>
                                                                        </>
                                                                    )}
                                                                </Listbox.Option>
                                                            ))
                                                        )}
                                                    </Listbox.Options>
                                                </div>
                                            </Listbox>

                                            {loading && (
                                                <Loader2 className="absolute right-8 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-indigo-300 animate-spin" />
                                            )}
                                        </div>
                                    </div>

                                    {/* COURIER DROPDOWN — SECOND */}
                                    <div className="flex-1">
                                        <label className="text-[8px] font-medium text-gray-500 dark:text-indigo-200/80 mb-1 block uppercase tracking-wide">
                                            Select Courier
                                        </label>

                                        <div className="relative">
                                            <Listbox value={selectedCourier} onChange={setSelectedCourier}>
                                                <div className="relative">
                                                    <Listbox.Button className="w-full px-2.5 py-2 md:py-1.5 bg-white dark:bg-[#1a1a2e] border border-gray-300 dark:border-indigo-400/50 rounded-md flex justify-between items-center text-[11px] text-black dark:text-white hover:border-black dark:hover:border-indigo-400 transition-colors">
                                                        <span className={selectedCourier ? "text-black dark:text-white" : "text-gray-400 dark:text-indigo-300/50"}>
                                                            {selectedCourier || "Select Courier"}
                                                        </span>
                                                        <ChevronDown className="w-3.5 h-3.5 text-gray-400 dark:text-indigo-300" />
                                                    </Listbox.Button>

                                                    <Listbox.Options className="absolute z-50 mt-1.5 w-full bg-white dark:bg-gradient-to-br dark:from-[#0b0b14] dark:via-[#0d0d18] dark:to-[#0b0b14] border border-black dark:border-indigo-500/40 rounded-md shadow-[0_4px_20px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_40px_rgba(79,70,229,0.35)] max-h-60 overflow-auto">
                                                        {["Delhivery", "XpressBees", "Gati", "BlueDart", "Ecom Express"].map((option) => (
                                                            <Listbox.Option
                                                                key={option}
                                                                value={option}
                                                                className={({ active }) =>
                                                                    `cursor-pointer px-2.5 py-1.5 border-b border-gray-50 dark:border-indigo-500/15 last:border-none text-[11px] ${active ? "bg-black dark:bg-indigo-600 text-white" : "text-black dark:text-indigo-100"
                                                                    }`
                                                                }
                                                            >
                                                                {option}
                                                            </Listbox.Option>
                                                        ))}
                                                    </Listbox.Options>
                                                </div>
                                            </Listbox>
                                        </div>
                                    </div>

                                </div>


                                {/* Pickup Date */}
                                <div className="mb-5 mt-4 md:mt-2">
                                    <label className="block text-[8px] font-medium text-gray-500 dark:text-indigo-200/80 mb-2 uppercase tracking-wide">
                                        Pickup Date
                                    </label>

                                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-1.5">
                                        {dates.map((dateInfo, index) => (
                                            <button
                                                key={index}
                                                onClick={() => setSelectedDate(index)}
                                                className={`flex flex-col items-center py-2.5 md:py-2 rounded-md border text-[10px] transition-all ${selectedDate === index
                                                    ? "border-black bg-black dark:border-indigo-500 dark:bg-gradient-to-br dark:from-indigo-600 dark:to-purple-600 dark:shadow-lg dark:shadow-indigo-900/50"
                                                    : "border-gray-200 dark:border-indigo-500/30 hover:border-black dark:hover:border-indigo-400 bg-white dark:bg-[#1a1a2e]"
                                                    }`}
                                            >
                                                <span className={`${selectedDate === index ? "text-gray-300 dark:text-indigo-100" : "text-gray-400 dark:text-indigo-300/60"}`}>
                                                    {dateInfo.day}
                                                </span>
                                                <span className={`text-base font-bold leading-tight ${selectedDate === index ? "text-white" : "text-black dark:text-white"}`}>
                                                    {dateInfo.date}
                                                </span>
                                                <span className={`${selectedDate === index ? "text-gray-300 dark:text-indigo-100" : "text-gray-400 dark:text-indigo-300/60"}`}>
                                                    {dateInfo.month}
                                                </span>
                                            </button>
                                        ))}
                                    </div>

                                    <div className="flex items-start gap-1.5 mt-3 md:mt-2.5 p-2 bg-gray-50 dark:bg-indigo-500/[0.08] border border-gray-200 dark:border-indigo-500/25 rounded-md">
                                        <Info className="w-3 h-3 text-black dark:text-indigo-300 mt-0.5 flex-shrink-0" />
                                        <p className="text-[10px] text-gray-600 dark:text-indigo-200/80">
                                            Book before 2 PM for same-day pickup.
                                        </p>
                                    </div>
                                </div>

                                {/* Pickup Slot + Boxes */}
                                <div className="mb-5">
                                    <div className="flex flex-col md:flex-row md:items-center md:gap-4">

                                        {/* Time Slot */}
                                        <div className="flex-1 mb-4 md:mb-0">
                                            <label className="block text-[8px] font-medium text-gray-500 dark:text-indigo-200/80 mb-2 uppercase tracking-wide">
                                                Pickup Time Slot
                                            </label>
                                            <div className="relative">
                                                <select
                                                    value={selectedSlot}
                                                    onChange={(e) => setSelectedSlot(e.target.value)}
                                                    className="w-full px-2.5 py-2 md:py-1.5 bg-white dark:bg-[#1a1a2e] border border-gray-300 dark:border-indigo-400/50 rounded-md text-[11px] text-black dark:text-white focus:outline-none focus:border-black dark:focus:border-indigo-400 appearance-none"
                                                >
                                                    <option value="">Select a time slot</option>
                                                    {pickupSlots.map((slot) => (
                                                        <option key={slot.value} value={slot.value}>
                                                            {slot.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-indigo-300 pointer-events-none" />
                                            </div>
                                        </div>

                                        {/* Boxes */}
                                        <div className="flex-1">
                                            <label className="block text-[8px] font-medium text-gray-500 dark:text-indigo-200/80 mb-2 uppercase tracking-wide">
                                                Number of Boxes
                                            </label>
                                            <input
                                                type="number"
                                                value={boxCount}
                                                onChange={(e) => setBoxCount(e.target.value)}
                                                className="w-full px-2.5 py-2 md:py-1.5 border border-gray-300 dark:border-indigo-400/50 rounded-md text-[11px] text-black dark:text-white bg-white dark:bg-[#1a1a2e] placeholder-gray-400 dark:placeholder-indigo-300/50 focus:outline-none focus:border-black dark:focus:border-indigo-400"
                                                min="1"
                                                placeholder="Enter box count"
                                            />
                                        </div>

                                    </div>
                                </div>

                                {/* Submit */}
                                <button
                                    onClick={handleCreateRequest}
                                    disabled={isSubmitting}
                                    className={`w-full py-2.5 md:py-2 rounded-md font-semibold text-[11px] tracking-wide uppercase transition-all ${isSubmitting
                                        ? "bg-gray-300 dark:bg-indigo-500/20 cursor-not-allowed text-white dark:text-indigo-300/60"
                                        : "bg-red-600 dark:bg-gradient-to-r dark:from-indigo-600 dark:to-purple-600 dark:hover:from-indigo-500 dark:hover:to-purple-500 dark:shadow-lg dark:shadow-indigo-900/50 hover:bg-gray-800 text-white"
                                        }`}
                                >
                                    {isSubmitting ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                            Creating...
                                        </span>
                                    ) : (
                                        "Create Pickup Request"
                                    )}
                                </button>

                            </div>
                        </div>
                        {/* Right Section - Table (Desktop) / Cards (Mobile) */}
                        <div className="lg:col-span-1 w-full">
                            <div className="bg-white dark:bg-gradient-to-br dark:from-[#0b0b14] dark:via-[#0d0d18] dark:to-[#0b0b14] w-full rounded-xl border border-gray-200 dark:border-indigo-500/30 dark:shadow-[0_4px_30px_rgba(79,70,229,0.2)] overflow-hidden">
                                <div className="p-3">
                                    {loading ? (
                                        <div className="flex flex-col items-center justify-center py-20">
                                            <Loader2 className="w-8 h-8 text-black dark:text-indigo-300 animate-spin mb-3" />
                                            <p className="text-gray-500 dark:text-indigo-300/70 text-[11px]">Loading pickup requests...</p>
                                        </div>
                                    ) : pickupRequests.length > 0 ? (
                                        <>
                                            {/* ================= DESKTOP TABLE (md and above) ================= */}
                                            <div className="hidden md:block">
                                                <div className="overflow-x-auto max-h-[420px] overflow-y-auto custom-scrollbar rounded-lg border border-gray-200 dark:border-indigo-500/25">
                                                    <table className="min-w-full text-left">
                                                        <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gradient-to-r dark:from-indigo-950 dark:via-purple-950 dark:to-indigo-950">
                                                            <tr>
                                                                <th className="px-3 py-2.5 text-left text-[9px] font-bold text-gray-600 dark:text-indigo-100 uppercase tracking-wider border-b border-gray-200 dark:border-indigo-400/40">
                                                                    Request ID
                                                                </th>
                                                                <th className="px-3 py-2.5 text-left text-[9px] font-bold text-gray-600 dark:text-indigo-100 uppercase tracking-wider border-b border-gray-200 dark:border-indigo-400/40">
                                                                    Pickup ID
                                                                </th>
                                                                <th className="px-3 py-2.5 text-left text-[9px] font-bold text-gray-600 dark:text-indigo-100 uppercase tracking-wider border-b border-gray-200 dark:border-indigo-400/40">
                                                                    Status
                                                                </th>
                                                                <th className="px-3 py-2.5 text-left text-[9px] font-bold text-gray-600 dark:text-indigo-100 uppercase tracking-wider border-b border-gray-200 dark:border-indigo-400/40">
                                                                    Date
                                                                </th>
                                                                <th className="px-3 py-2.5 text-left text-[9px] font-bold text-gray-600 dark:text-indigo-100 uppercase tracking-wider border-b border-gray-200 dark:border-indigo-400/40">
                                                                    Time
                                                                </th>
                                                                <th className="px-3 py-2.5 text-center text-[9px] font-bold text-gray-600 dark:text-indigo-100 uppercase tracking-wider border-b border-gray-200 dark:border-indigo-400/40">
                                                                    Boxes
                                                                </th>
                                                            </tr>
                                                        </thead>

                                                        <tbody className="divide-y divide-gray-100 dark:divide-indigo-500/15">
                                                            {pickupRequests.map((request, idx) => (
                                                                <tr
                                                                    key={request.id}
                                                                    onClick={() => setSelectedRequest(request.request_id)}
                                                                    className={`cursor-pointer transition-colors ${selectedRequest === request.request_id
                                                                            ? "bg-indigo-50 dark:bg-indigo-500/15"
                                                                            : idx % 2 === 0
                                                                                ? "bg-white dark:bg-[#0b0b14]"
                                                                                : "bg-gray-50/60 dark:bg-indigo-500/[0.04]"
                                                                        } hover:bg-gray-100 dark:hover:bg-indigo-500/10`}
                                                                >
                                                                    <td className="px-3 py-2.5 text-[10px] font-semibold text-gray-800 dark:text-indigo-100 whitespace-nowrap">
                                                                        {request.request_id}
                                                                    </td>
                                                                    <td className="px-3 py-2.5 text-[10px] font-medium text-gray-700 dark:text-indigo-200 whitespace-nowrap">
                                                                        {request.pickup_id}
                                                                    </td>
                                                                    <td className="px-3 py-2.5">
                                                                        <span
                                                                            className={`px-2 py-0.5 inline-flex items-center text-[9px] font-bold rounded-md border ${getStatusColor(
                                                                                request.status
                                                                            )}`}
                                                                        >
                                                                            {request.status}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-3 py-2.5 text-[10px] font-medium text-gray-700 dark:text-indigo-200 whitespace-nowrap">
                                                                        {request.pickup_date}
                                                                    </td>
                                                                    <td className="px-3 py-2.5 text-[10px] font-medium text-gray-700 dark:text-indigo-200 whitespace-nowrap">
                                                                        {request.start_time}
                                                                    </td>
                                                                    <td className="px-3 py-2.5 text-[10px] font-semibold text-center text-gray-800 dark:text-indigo-100">
                                                                        {request.box_count}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>

                                            {/* ================= MOBILE CARDS (below md) ================= */}
                                            <div className="md:hidden flex flex-col gap-3 max-h-[420px] overflow-y-auto custom-scrollbar pr-1">
                                                {pickupRequests.map((request) => (
                                                    <div
                                                        key={request.id}
                                                        onClick={() => setSelectedRequest(request.request_id)}
                                                        className={`cursor-pointer rounded-xl border transition-all duration-200 p-3.5 ${
                                                            selectedRequest === request.request_id
                                                                ? "bg-indigo-50 dark:bg-indigo-500/15 border-indigo-300 dark:border-indigo-500/50 shadow-md"
                                                                : "bg-white dark:bg-[#0b0b14] border-gray-200 dark:border-indigo-500/30 hover:border-indigo-300 dark:hover:border-indigo-500/50"
                                                        }`}
                                                    >
                                                        {/* Top Row: Request ID + Status */}
                                                        <div className="flex items-center justify-between mb-2.5">
                                                            <div className="flex flex-col">
                                                                <span className="text-[9px] font-medium text-gray-400 dark:text-indigo-300/60 uppercase tracking-wider">
                                                                    Request ID
                                                                </span>
                                                                <span className="text-[12px] font-bold text-gray-800 dark:text-indigo-100">
                                                                    {request.request_id}
                                                                </span>
                                                            </div>
                                                            <span
                                                                className={`px-2 py-0.5 inline-flex items-center text-[9px] font-bold rounded-md border ${getStatusColor(request.status)}`}
                                                            >
                                                                {request.status}
                                                            </span>
                                                        </div>

                                                        {/* Middle Row: Pickup ID */}
                                                        <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-100 dark:border-indigo-500/20">
                                                            <span className="text-[10px] text-gray-500 dark:text-indigo-300/70">
                                                                Pickup ID
                                                            </span>
                                                            <span className="text-[11px] font-semibold text-gray-700 dark:text-indigo-200">
                                                                {request.pickup_id}
                                                            </span>
                                                        </div>

                                                        {/* Bottom Row: Date, Time, Boxes */}
                                                        <div className="grid grid-cols-3 gap-2">
                                                            <div className="flex flex-col">
                                                                <span className="text-[9px] text-gray-400 dark:text-indigo-300/60 uppercase">Date</span>
                                                                <span className="text-[10px] font-semibold text-gray-700 dark:text-indigo-200">
                                                                    {request.pickup_date}
                                                                </span>
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <span className="text-[9px] text-gray-400 dark:text-indigo-300/60 uppercase">Time</span>
                                                                <span className="text-[10px] font-semibold text-gray-700 dark:text-indigo-200">
                                                                    {request.start_time}
                                                                </span>
                                                            </div>
                                                            <div className="flex flex-col items-end">
                                                                <span className="text-[9px] text-gray-400 dark:text-indigo-300/60 uppercase">Boxes</span>
                                                                <span className="text-[11px] font-bold text-gray-800 dark:text-indigo-100">
                                                                    {request.box_count}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Pagination (Common for both) */}
                                            <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50 dark:bg-indigo-500/[0.08] border border-gray-200 dark:border-indigo-500/25 mt-3 rounded-lg">
                                                <div className="text-[10px] font-medium text-gray-600 dark:text-indigo-200/80">
                                                    Page {currentPage} of {totalPages}
                                                </div>
                                                <div className="flex gap-2 md:gap-1.5">
                                                    <button
                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                        disabled={currentPage === 1}
                                                        className={`px-3 py-1.5 md:py-1 rounded-md text-[10px] font-semibold transition-colors ${currentPage === 1
                                                                ? 'bg-gray-200 dark:bg-indigo-500/15 text-gray-400 dark:text-indigo-300/40 cursor-not-allowed'
                                                                : 'bg-black dark:bg-gradient-to-r dark:from-indigo-600 dark:to-purple-600 text-white hover:bg-gray-800 dark:hover:from-indigo-500 dark:hover:to-purple-500 dark:shadow-md dark:shadow-indigo-900/40'
                                                            }`}
                                                    >
                                                        Previous
                                                    </button>
                                                    <button
                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                        disabled={currentPage === totalPages}
                                                        className={`px-3 py-1.5 md:py-1 rounded-md text-[10px] font-semibold transition-colors ${currentPage === totalPages
                                                                ? 'bg-gray-200 dark:bg-indigo-500/15 text-gray-400 dark:text-indigo-300/40 cursor-not-allowed'
                                                                : 'bg-red-600 dark:bg-gradient-to-r dark:from-indigo-600 dark:to-purple-600 text-white hover:bg-gray-800 dark:hover:from-indigo-500 dark:hover:to-purple-500 dark:shadow-md dark:shadow-indigo-900/40'
                                                            }`}
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center py-12">
                                            <Package className="w-8 h-8 text-gray-300 dark:text-indigo-300/40 mb-2" />
                                            <p className="text-black dark:text-indigo-100 text-[11px] font-medium">No pickup requests found</p>
                                            <p className="text-gray-400 dark:text-indigo-300/60 text-[10px] mt-0.5">There are no pickup requests to display</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default PickupDetailsPage;